import { useState } from 'react';
import { Button, Icon, Popover, useToast } from '@efira/ui';

import { useOverlayToken } from '../../api/queries';
import { LANDING_URL } from '../../config';

/**
 * Поповер постоянной ссылки оверлея.
 *
 * Состав по эталону: поле со ссылкой, кнопка «Копировать», строка «ссылка не
 * меняется» и карточка-ссылка на инструкцию по выходу в эфир.
 *
 * ССЫЛКА ЗАМАСКИРОВАНА ПО УМОЛЧАНИЮ - наше отличие, и оно не косметическое:
 * поповер открывается в консоли, а консоль у стримера часто на втором экране,
 * который попадает в захват. Открытая ссылка - это доступ к чужому оверлею.
 * Кнопка «показать» рядом.
 */
export function OverlayUrl() {
  const token = useOverlayToken();
  const toast = useToast();
  const [revealed, setRevealed] = useState(false);
  const url = token.data?.url ?? '';
  /**
   * Маскируется ХВОСТ, и буллиты обязаны попадать в видимую часть поля.
   *
   * Первая попытка - «начало ссылки плюс двенадцать буллитов» - выглядела на
   * экране так же, как раскрытая: поле узкое, `truncate` срезал строку
   * многоточием ДО буллитов, и кнопка «показать» не давала видимого эффекта.
   * Поэтому прячется только секрет - последний сегмент пути, - а видно, что он
   * спрятан.
   */
  const masked =
    url === ''
      ? ''
      : (() => {
          const cut = url.lastIndexOf('/');
          return cut === -1 ? '••••••••' : `${url.slice(0, cut + 1)}${'•'.repeat(10)}`;
        })();

  async function copy() {
    if (url === '') return;

    try {
      await navigator.clipboard.writeText(url);
      toast.show({ tone: 'success', title: 'Ссылка скопирована' });
    } catch {
      // Буфер недоступен (нет https, отказ в разрешении): раскрываем ссылку,
      // чтобы её можно было взять руками, а не оставляем в тупике.
      setRevealed(true);
      toast.show({
        tone: 'warning',
        title: 'Браузер не дал доступ к буферу',
        text: 'Ссылка раскрыта — скопируйте её вручную.',
      });
    }
  }

  return (
    <Popover
      label="Ссылка оверлея"
      triggerClassName="gap-2 whitespace-nowrap"
      className="w-[26rem] p-3"
      trigger={
        /* Пометка ради ворот лестницы: ступени полосы проверяются по
           элементам, а не по классам (`e2e/console-rows.spec.ts`). */
        <span data-topbar="overlay-link" className="inline-flex items-center gap-2">
          <Icon name="link" size="sm" />
          {/*
            ПОРОГ 620, А НЕ 720: ПОДПИСЬ ПРЯТАЛАСЬ ТАМ, ГДЕ МЕСТА БОЛЬШЕ.

            Находка аудита единицы 16 (04): на 1470 подписи «Ссылка оверлея»
            не было, а на 820 была. Центральная треть на заводских границах
            1470 - 647 px, то есть МЕНЬШЕ прежнего порога 720, и главный шаг
            первого пути оставался безымянной иконкой ровно на самом широком
            экране. На 820 консоль одноколоночная, полоса во всю ширину окна -
            и подпись возвращалась.

            620 держит подпись на заводских границах и сохраняет порядок
            ступеней: подпись «Виджеты» (800) уходит раньше, метка «Pro»
            (600) - позже. Лестницу сторожит `e2e/console-rows.spec.ts`.

            ПОДПИСЬ - ОДНО СЛОВО. «Ссылка оверлея» в полосе стоила 105 px, и
            на заводской трети 647 px название игры начинало сокращаться
            раньше, чем у соседей кончалось, что снимать, - то самое правило
            лестницы. Полное имя осталось у кнопки (`label` поповера) и
            достаётся чтецу; в полосе стоит объект, а не действие с ним.
          */}
          <span className="@max-[500px]:hidden">Оверлей</span>
          {/* Стрелка уходит вместе с подписью «Виджеты» (800): она ничего
              не называет, а стоит 20 px - ровно тех, которых не хватало
              названию игры на заводской трети. */}
          <Icon name="chevronDown" size="sm" className="@max-[800px]:hidden" />
        </span>
      }
    >
      <p className="mb-2 text-sm font-medium">Ссылка оверлея</p>

      <div className="flex items-center gap-2">
        <output
          data-topbar="overlay-url-field"
          aria-label="Ссылка оверлея"
          className="block min-w-0 flex-1 truncate font-mono text-xs"
        >
          {token.data === undefined ? 'Загрузка…' : revealed ? url : masked}
        </output>
        <Button
          variant="ghost"
          size="s"
          iconOnly
          aria-label={revealed ? 'Скрыть ссылку' : 'Показать ссылку'}
          onClick={() => {
            setRevealed((value) => !value);
          }}
        >
          <Icon name={revealed ? 'eyeOff' : 'eye'} size="sm" />
        </Button>
        <Button
          variant="primary"
          size="s"
          data-topbar="overlay-url-copy"
          onClick={() => {
            void copy();
          }}
        >
          Копировать
        </Button>
      </div>

      <p data-topbar="overlay-url-note" className="text-foreground/80 mt-2 text-sm">
        Ссылка не меняется. Настройте её один раз и используйте каждый эфир.
      </p>

      <a
        data-topbar="overlay-url-guide"
        href={`${LANDING_URL}/docs/browser-source`}
        target="_blank"
        rel="noreferrer"
        className="border-foreground/10 hover:bg-muted/60 mt-3 flex items-center gap-3 rounded-lg border p-3"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium">Как вывести в эфир</span>
          <span className="text-foreground/80 block text-xs">
            OBS, Streamlabs, TikTok LIVE Studio или телефон
          </span>
        </span>
        <Icon name="arrowRight" size="sm" />
      </a>
    </Popover>
  );
}
